/**
 * KostentraegerExportDialog
 *
 * Dialog zur Auswahl der Räume, die in der offiziellen Kostenträger-Meldung
 * (XLSX) exportiert werden sollen. Spalten exakt nach Vorgabe:
 *   - Art der Räumlichkeit
 *   - Raumnummer / Raumbezeichnung
 *   - Größe in m²
 *   - Anzahl Teilnehmende bzw. Plätze
 *   - Ausstattung
 *   - Nachweis (Eigentum / Mietobjekt)
 */
import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Search, FileSpreadsheet, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const OWNERSHIP_LABEL = {
  owned: 'Eigentum',
  rented: 'Mietobjekt',
  '': '—',
};

export const KostentraegerExportDialog = ({
  open, onOpenChange, rooms, defaultSelectedIds = null,
}) => {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);
  const [onlyComplete, setOnlyComplete] = useState(false);

  // Reset bei Open: standardmäßig alle aktiven Räume vorselektieren
  useEffect(() => {
    if (open) {
      const ids = defaultSelectedIds
        ? new Set(defaultSelectedIds)
        : new Set(rooms.filter((r) => r.is_active).map((r) => r.id));
      setSelectedIds(ids);
      setSearch('');
      setOnlyComplete(false);
    }
  }, [open, rooms, defaultSelectedIds]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rooms.filter((r) => {
      if (q && !`${r.name} ${r.room_type || ''} ${r.building || ''} ${r.location_name || ''}`
        .toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rooms, search]);

  const allSelected = filtered.length > 0 && filtered.every((r) => selectedIds.has(r.id));
  const someSelected = filtered.some((r) => selectedIds.has(r.id)) && !allSelected;

  const toggleAll = () => {
    const next = new Set(selectedIds);
    if (allSelected) {
      filtered.forEach((r) => next.delete(r.id));
    } else {
      filtered.forEach((r) => next.add(r.id));
    }
    setSelectedIds(next);
  };

  const toggleRow = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const isRoomComplete = (r) =>
    !!r.room_type && !!r.size_sqm && !!r.capacity && !!r.ownership_type;

  const incompleteSelected = useMemo(
    () => Array.from(selectedIds)
      .map((id) => rooms.find((r) => r.id === id))
      .filter((r) => r && !isRoomComplete(r)),
    [selectedIds, rooms]
  );

  const handleExport = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      toast.error('Bitte mindestens einen Raum auswählen');
      return;
    }
    setExporting(true);
    try {
      const res = await api.post(
        '/rooms/export-kostentraeger',
        { room_ids: ids },
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Filename aus Content-Disposition lesen (falls vorhanden)
      const cd = res.headers?.['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/);
      a.download = match ? match[1] : 'Kostentraeger-Meldung.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`Export erstellt — ${ids.length} Räume`);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Export fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90dvh] flex flex-col p-0">
        <DialogHeader className="px-4 sm:px-6 pt-5 sm:pt-6 pb-3 border-b border-border">
          <DialogTitle className="font-heading flex items-center gap-2 text-base sm:text-xl">
            <FileSpreadsheet className="h-5 w-5 text-primary shrink-0" />
            Kostenträger-Meldung exportieren
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            Wählen Sie die Räumlichkeiten aus, die in der offiziellen Aufstellung an den
            Kostenträger gemeldet werden sollen.
          </DialogDescription>
        </DialogHeader>

        <div className="px-4 sm:px-6 py-3 border-b border-border space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              type="search"
              placeholder="Filtern nach Name, Typ, Gebäude, Standort..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="kostentraeger-search"
            />
          </div>
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Checkbox
                id="kt-select-all"
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = someSelected; }}
                onCheckedChange={toggleAll}
                data-testid="kostentraeger-select-all"
              />
              <label htmlFor="kt-select-all" className="cursor-pointer select-none font-medium">
                Alle ({filtered.length}) {allSelected ? 'abwählen' : 'auswählen'}
              </label>
            </div>
            <Badge variant="secondary" className="font-medium">
              {selectedIds.size} ausgewählt
            </Badge>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-2 max-h-[50vh]">
          {filtered.length === 0 && (
            <p className="text-center text-muted-foreground py-8">
              Keine Räume gefunden
            </p>
          )}
          <div className="divide-y divide-border">
            {filtered.map((room) => {
              const complete = isRoomComplete(room);
              const checked = selectedIds.has(room.id);
              return (
                <label
                  key={room.id}
                  className="flex items-start gap-3 py-3 cursor-pointer hover:bg-secondary/30 rounded-md px-2 -mx-2"
                  data-testid={`kostentraeger-room-${room.id}`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggleRow(room.id)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{room.name}</span>
                      {room.room_type && (
                        <Badge variant="outline" className="text-xs">{room.room_type}</Badge>
                      )}
                      {!complete && (
                        <Badge variant="outline" className="text-xs text-amber-700 border-amber-300 bg-amber-50">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          unvollständig
                        </Badge>
                      )}
                    </div>
                    <div className="mt-1 grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <span>
                        <strong className="text-foreground">Größe:</strong>{' '}
                        {room.size_sqm ? `${room.size_sqm} m²` : '—'}
                      </span>
                      <span>
                        <strong className="text-foreground">Plätze:</strong> {room.capacity || '—'}
                      </span>
                      <span>
                        <strong className="text-foreground">Nachweis:</strong>{' '}
                        {OWNERSHIP_LABEL[room.ownership_type || ''] || '—'}
                      </span>
                      <span className="truncate">
                        <strong className="text-foreground">Standort:</strong>{' '}
                        {room.location_name || '—'}
                      </span>
                    </div>
                    {room.equipment?.length > 0 && (
                      <div className="mt-1 text-xs text-muted-foreground truncate">
                        <strong className="text-foreground">Ausstattung:</strong>{' '}
                        {room.equipment.join(', ')}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {incompleteSelected.length > 0 && (
          <div className="px-4 sm:px-6 py-2 bg-amber-50 dark:bg-amber-950/20 border-t border-amber-200 dark:border-amber-900/40 text-xs text-amber-900 dark:text-amber-200">
            <AlertCircle className="h-3.5 w-3.5 inline -mt-0.5 mr-1" />
            {incompleteSelected.length} Raum/Räume in der Auswahl haben unvollständige Stammdaten.
            Diese Felder erscheinen leer im Export.
          </div>
        )}

        <div className="px-4 sm:px-6 py-4 border-t border-border flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
          <Button
            variant="outline"
            className="rounded-full w-full sm:w-auto"
            onClick={() => onOpenChange(false)}
            disabled={exporting}
          >
            Abbrechen
          </Button>
          <Button
            className="rounded-full w-full sm:w-auto"
            onClick={handleExport}
            disabled={exporting || selectedIds.size === 0}
            data-testid="kostentraeger-export-btn"
          >
            {exporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Wird erstellt...
              </>
            ) : (
              <>
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                XLSX herunterladen ({selectedIds.size})
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
